import { useState } from 'react'
import { PermissionButton } from '../components/Permissions.js'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useApi, useAsync } from '../lib/useApi.js'
import { ApiError, type EdgeFunction } from '../lib/api.js'
import { UTC_NOTE, check, scheduleLabel } from '../lib/schedules.js'
import { CopyButton, Dialog, PageHeader, Empty, ErrorBox, Loading, relative } from '../components/ui.js'
import { ViewNav } from '../components/ViewNav.js'
import Secrets from '../components/Secrets.js'

/**
 * What this project is serving to the world, and on what terms.
 *
 * WHY THIS PAGE IS MOSTLY READ-ONLY
 *
 * Deploying needs files, and a browser is a poor place to hand over a
 * directory of them. The CLI does that well. What a browser IS good for is
 * the question nobody could answer until now: what is actually deployed
 * right now, and is any of it public?
 *
 * So this page shows state and offers the destructive actions - the two
 * things a person genuinely wants a screen for - and prints the command for
 * everything else rather than pretending to be a deploy tool.
 *
 * THE PUBLIC WARNING IS NOT DECORATION
 *
 * A function's URL takes no key. Someone who believes otherwise will put
 * something private behind it, and there is no error message for that. It
 * is said on the CLI at every deploy, in the MCP tool result, and here.
 */
export default function Deployments() {
  const { id = '' } = useParams()
  const api = useApi()

  const project = useAsync(() => api.getProject(id), [api, id])
  const [reloadKey, setReloadKey] = useState(0)
  const site = useAsync(() => api.site(id), [api, id, reloadKey])
  const functions = useAsync(() => api.listFunctions(id), [api, id, reloadKey])
  const [error, setError] = useState<string | null>(null)
  const [params] = useSearchParams()
  const requested = params.get('view') ?? 'site'
  const view = ['site', 'functions', 'environment'].includes(requested) ? requested : 'site'
  const [target, setTarget] = useState<{ kind: 'site' | 'function'; name: string } | null>(null)
  const [busy, setBusy] = useState(false)
  /** The function whose schedule is being edited, and the box's contents. */
  const [scheduling, setScheduling] = useState<EdgeFunction | null>(null)
  const [expression, setExpression] = useState('')

  const p = project.data?.project
  const origin = p?.dataPlaneUrl ?? null

  async function saveSchedule(fn: EdgeFunction, schedule: string | null) {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await api.setFunctionSchedule(id, fn.name, schedule)
      setScheduling(null)
      setReloadKey((k) => k + 1)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that schedule.')
    } finally {
      setBusy(false)
    }
  }

  async function removeSite() {
    setError(null)
    setBusy(true)
    try {
      await api.deleteSite(id)
      setReloadKey((k) => k + 1)
      setTarget(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove the site.')
    } finally {
      setBusy(false)
    }
  }

  async function removeFunction(name: string) {
    setError(null)
    setBusy(true)
    try {
      await api.deleteFunction(id, name)
      setReloadKey((k) => k + 1)
      setTarget(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that function.')
    } finally {
      setBusy(false)
    }
  }

  if (project.loading) return <Loading />
  if (project.error) return <ErrorBox>{project.error}</ErrorBox>
  if (!p) return <Empty>Project not found.</Empty>

  return (
    <>
      <PageHeader
        title="Deployments"
        description="Manage your published site, edge functions, and environment."
        actions={
          <Link className="btn" to={`/projects/${id}/diagnostics`}>
            Diagnostics
          </Link>
        }
      />
      <ViewNav
        current={view}
        items={[
          ['site', 'Site'],
          ['functions', 'Functions'],
          ['environment', 'Environment'],
        ]}
      />
      {view === 'site' && (
        <>
          <h2>Static site</h2>
          <div className="card" style={{ padding: 16, marginBottom: 24 }}>
            {site.loading ? (
              <Loading />
            ) : site.error ? (
              <ErrorBox retry={site.reload}>{site.error}</ErrorBox>
            ) : !site.data?.deployed ? (
              <>
                <p className="sub" style={{ margin: '0 0 10px' }}>
                  No site deployed.
                </p>
                <pre className="secret" style={{ margin: 0 }}>
                  nimbly deploy ./dist
                </pre>
                <p className="sub small">Run from your linked project directory after building your site.</p>
                <CopyButton value="nimbly deploy ./dist" label="Copy deploy command" />
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                  <span className="pill">{site.data.files} files</span>
                  <span className="sub small">deployed {relative(site.data.deployedAt!)}</span>
                  {site.data.singlePageApp && <span className="pill">single-page app</span>}
                </div>
                {origin && (
                  <div className="endpoint-line">
                    <code>{origin}/</code>
                    <CopyButton value={`${origin}/`} label="Copy URL" />
                  </div>
                )}
                <p className="sub small" style={{ margin: '0 0 10px' }}>
                  {site.data.singlePageApp
                    ? 'Unknown paths serve index.html — your router owns them.'
                    : 'Unknown paths return 404.'}
                </p>
                <PermissionButton
                  permission="project:update"
                  className="btn btn-danger"
                  onClick={() => {
                    setError(null)
                    setTarget({ kind: 'site', name: p.name })
                  }}
                >
                  Stop serving
                </PermissionButton>
              </>
            )}
          </div>
        </>
      )}
      {view === 'functions' && (
        <>
          <h2>Edge functions</h2>
          <div className="info-banner" style={{ marginBottom: 16 }}>
            <strong>Every function URL is public.</strong>
            <p className="sub small" style={{ margin: '6px 0 0' }}>
              No NimblyBase key is required to call one — a webhook handler that demanded one could not
              receive a webhook. Your code decides who may call it: check a signature or a shared secret
              inside the function.
            </p>
          </div>

          <div className="card" style={{ marginBottom: 24 }}>
            {functions.loading ? (
              <Loading />
            ) : functions.error ? (
              <ErrorBox retry={functions.reload}>{functions.error}</ErrorBox>
            ) : !functions.data?.functions.length ? (
              <div style={{ padding: 16 }}>
                <p className="sub" style={{ margin: '0 0 10px' }}>
                  No functions deployed.
                </p>
                <pre className="secret" style={{ margin: 0 }}>
                  nimbly functions deploy ./webhook.js
                </pre>
                <p className="sub small">
                  Deploy from your linked project directory. Build history and Git integration are not
                  available in the dashboard yet.
                </p>
                <CopyButton value="nimbly functions deploy ./webhook.js" label="Copy deploy command" />
              </div>
            ) : (
              functions.data.functions.map((fn) => (
                <div className="row" key={fn.name}>
                  <div className="row-main">
                    <div className="row-title">
                      {fn.name}{' '}
                      <span className={fn.status === 'failed' ? 'pill pill-dead' : 'pill'}>{fn.status}</span>
                    </div>
                    <div className="row-sub">
                      {scheduleLabel(fn.schedule)} · v{fn.version} · updated {relative(fn.updatedAt)}
                      {origin && (
                        <>
                          {' '}
                          ·{' '}
                          <span className="mono">
                            {origin}/fn/{fn.name}
                          </span>
                        </>
                      )}
                    </div>
                    {fn.failureMessage && (
                      <div className="row-sub" style={{ color: 'var(--red)' }}>
                        {fn.failureMessage}
                      </div>
                    )}
                  </div>
                  {origin && <CopyButton value={`${origin}/fn/${fn.name}`} label="Copy URL" />}
                  <PermissionButton
                    permission="apikey:create"
                    className="btn"
                    onClick={() => {
                      setError(null)
                      setExpression(fn.schedule ?? '')
                      setScheduling(fn)
                    }}
                  >
                    Schedule
                  </PermissionButton>
                  <PermissionButton
                    permission="apikey:create"
                    className="btn btn-danger"
                    onClick={() => {
                      setError(null)
                      setTarget({ kind: 'function', name: fn.name })
                    }}
                  >
                    Delete
                  </PermissionButton>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Environment variables live here rather than on the project page:
          they are what a function reads, and this is the page about what
          functions are doing. */}
      {view === 'environment' && <Secrets projectId={id} />}
      <Dialog
        open={scheduling !== null}
        title={scheduling ? `When should ${scheduling.name} run?` : ''}
        busy={busy}
        onClose={() => setScheduling(null)}
      >
        <div className="field">
          <label htmlFor="fn-schedule">Schedule</label>
          <input
            id="fn-schedule"
            className="input"
            value={expression}
            placeholder="0 * * * *"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => setExpression(e.target.value)}
            style={{ width: '100%', fontFamily: 'var(--mono)' }}
          />
          <p className="sub small" style={{ margin: '6px 0 0' }}>
            Five fields: minute, hour, day of month, month, day of week. {UTC_NOTE}
          </p>
          {expression.trim() !== '' && (
            <p className="sub small" style={{ margin: '6px 0 0' }}>
              {check(expression).ok
                ? `Runs ${(check(expression) as { describes: string }).describes}.`
                : (check(expression) as { why: string }).why}
            </p>
          )}
        </div>
        {error && <ErrorBox>{error}</ErrorBox>}
        <button
          className="btn btn-primary"
          disabled={busy || !check(expression).ok}
          onClick={() => { if (scheduling) void saveSchedule(scheduling, expression.trim()) }}
        >
          {busy ? 'Saving…' : 'Save schedule'}
        </button>{' '}
        {scheduling?.schedule && (
          <button
            className="btn"
            disabled={busy}
            onClick={() => { if (scheduling) void saveSchedule(scheduling, null) }}
          >
            Stop running it on a schedule
          </button>
        )}
      </Dialog>

      <Dialog
        open={target !== null}
        title={target?.kind === 'site' ? 'Stop serving this site?' : `Delete ${target?.name ?? 'function'}?`}
        busy={busy}
        onClose={() => setTarget(null)}
      >
        <p className="sub">
          {target?.kind === 'site'
            ? 'Published files will be removed and the site will stop serving. Deploy again from your local source to restore it.'
            : 'The function code will be removed and its endpoint will stop serving. Keep a local copy before deleting.'}
        </p>
        {error && <ErrorBox>{error}</ErrorBox>}
        <div className="actions">
          <button className="btn" disabled={busy} onClick={() => setTarget(null)}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            disabled={busy}
            onClick={() => {
              if (!busy && target) void (target.kind === 'site' ? removeSite() : removeFunction(target.name))
            }}
          >
            {busy ? 'Removing…' : 'Confirm removal'}
          </button>
        </div>
      </Dialog>
    </>
  )
}
